/**
 * Enhanced UTM Tracker - Robust utility to capture and store UTM parameters
 * Uses multiple storage methods (localStorage, cookies, sessionStorage) with fallbacks
 * for maximum cross-browser and cross-device compatibility.
 */

// List of UTM parameters to track
const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'utm_campaign_id',
  'utm_asset_group',
  'utm_creative',
  'utm_adset_id',
  'utm_ad_id',
  'utm_device',
  'utm_network',
  'utm_placement',
  'utm_location',
  'fbclid', // Facebook click ID
  'gclid', // Google click ID
  'gbraid', // Google app cross-network click ID
  'wbraid', // Google web cross-network click ID
  'msclkid', // Microsoft click ID
  'ttclid', // TikTok click ID
  'twclid', // Twitter click ID
  'li_fat_id', // LinkedIn click ID
  'source', // Generic source parameter
  'ref', // Referrer
  'aff',
  'affiliate',
  'affiliate_code',
  'affiliateCode',
];

const AFFILIATE_PARAMS = [
  'affiliate_code',
  'affiliateCode',
  'aff',
  'affiliate',
];

// Attribution identifiers we always want to preserve for downstream APIs
const ATTRIBUTION_PARAMS = [
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'msclkid',
  'ttclid',
  'twclid',
  'li_fat_id',
  'aff',
  'affiliate',
  'affiliate_code',
  'affiliateCode',
];

// Storage keys
const STORAGE_KEYS = {
  LOCAL: 'lumiwealth_utm_data',
  SESSION: 'lumiwealth_utm_session',
  COOKIE: 'lumiwealth_utm_cookie',
  AFFILIATE: 'lumiwealth_affiliate_code'
};

// Default expiration time (30 days in milliseconds)
const DEFAULT_EXPIRATION_TIME = 30 * 24 * 60 * 60 * 1000; // 30 days
const COOKIE_EXPIRATION_DAYS = 30;

export const normalizeAffiliateCode = (value) => {
  const normalized = String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9_-]/g, '')
    .slice(0, 32);
  return normalized || null;
};

export const extractAffiliateCodeFromUrl = () => {
  if (typeof window === 'undefined' || !window.location) return null;

  try {
    const urlParams = new URLSearchParams(window.location.search || '');
    for (const param of AFFILIATE_PARAMS) {
      if (urlParams.has(param)) {
        return normalizeAffiliateCode(urlParams.get(param));
      }
    }
  } catch (error) {
    console.error('Error extracting affiliate code:', error);
  }

  return null;
};

/**
 * Extract UTM parameters from current URL
 */
export const extractUtmParams = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const utmData = {};
    let hasUtmParams = false;

    // Extract each UTM parameter
    UTM_PARAMS.forEach(param => {
      if (urlParams.has(param)) {
        utmData[param] = urlParams.get(param);
        hasUtmParams = true;
      }
    });

    // If we have UTM params, also capture landing page and referrer
    if (hasUtmParams) {
      utmData.landing_page = window.location.href.split('?')[0];
      utmData.referrer = document.referrer || 'direct';
      utmData.timestamp = Date.now();
      utmData.first_seen_date = new Date().toISOString();
    }

    return hasUtmParams ? utmData : null;
  } catch (error) {
    console.error('Error extracting UTM parameters:', error);
    return null;
  }
};

/**
 * Set a cookie with the provided name, value and expiration days
 */
const setCookie = (name, value, expirationDays) => {
  try {
    const d = new Date();
    d.setTime(d.getTime() + (expirationDays * 24 * 60 * 60 * 1000));
    
    // Use secure and SameSite attributes for better security
    const expires = `expires=${d.toUTCString()}`;
    const sameSite = window.location.protocol === 'https:' ? 'SameSite=None; Secure' : 'SameSite=Lax';
    
    // Set the cookie
    document.cookie = `${name}=${value};${expires};path=/;${sameSite}`;
    return true;
  } catch (error) {
    console.error('Error setting cookie:', error);
    return false;
  }
};

/**
 * Get a cookie value by name
 */
const getCookie = (name) => {
  try {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        return c.substring(nameEQ.length, c.length);
      }
    }
    return null;
  } catch (error) {
    console.error('Error getting cookie:', error);
    return null;
  }
};

export const storeAffiliateCode = (code) => {
  const normalized = normalizeAffiliateCode(code);
  if (!normalized) return null;

  try {
    localStorage.setItem(STORAGE_KEYS.AFFILIATE, normalized);
  } catch (error) {
    console.warn('Unable to store affiliate code in localStorage:', error);
  }

  try {
    sessionStorage.setItem(STORAGE_KEYS.AFFILIATE, normalized);
  } catch (error) {
    console.warn('Unable to store affiliate code in sessionStorage:', error);
  }

  setCookie(STORAGE_KEYS.AFFILIATE, encodeURIComponent(normalized), COOKIE_EXPIRATION_DAYS);
  return normalized;
};

export const getStoredAffiliateCode = () => {
  try {
    const localValue = normalizeAffiliateCode(localStorage.getItem(STORAGE_KEYS.AFFILIATE));
    if (localValue) return localValue;
  } catch (error) {
    console.warn('Unable to read affiliate code from localStorage:', error);
  }

  try {
    const sessionValue = normalizeAffiliateCode(sessionStorage.getItem(STORAGE_KEYS.AFFILIATE));
    if (sessionValue) return sessionValue;
  } catch (error) {
    console.warn('Unable to read affiliate code from sessionStorage:', error);
  }

  const cookieValue = getCookie(STORAGE_KEYS.AFFILIATE);
  return normalizeAffiliateCode(cookieValue ? decodeURIComponent(cookieValue) : null);
};

export const getAffiliateCodeForApi = () => {
  const currentCode = extractAffiliateCodeFromUrl();
  if (currentCode) {
    return storeAffiliateCode(currentCode);
  }

  return getStoredAffiliateCode();
};

export const captureAffiliateReferralClick = async (apiUrl) => {
  const affiliateCode = getAffiliateCodeForApi();
  if (!affiliateCode || !apiUrl || typeof window === 'undefined' || !window.sessionStorage) return null;

  const sessionKey = `lumiwealth_affiliate_click_recorded:${affiliateCode}`;
  try {
    if (window.sessionStorage.getItem(sessionKey)) return affiliateCode;
    window.sessionStorage.setItem(sessionKey, 'pending');
  } catch {
    return affiliateCode;
  }

  try {
    const response = await fetch(`${apiUrl}/affiliates/click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ affiliateCode }),
    });
    if (response.ok) {
      window.sessionStorage.setItem(sessionKey, 'true');
    } else {
      window.sessionStorage.removeItem(sessionKey);
    }
  } catch (error) {
    try {
      window.sessionStorage.removeItem(sessionKey);
    } catch {
      // Ignore storage cleanup failures; click tracking is best-effort.
    }
    console.debug('Affiliate click tracking unavailable:', error);
  }

  return affiliateCode;
};

/**
 * Delete a cookie by name
 */
const deleteCookie = (name) => {
  try {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;`;
    return true;
  } catch (error) {
    console.error('Error deleting cookie:', error);
    return false;
  }
};

/**
 * Try to use localStorage with fallback to memory
 */
const localStorageAvailable = (() => {
  try {
    const testKey = '__lw_storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
})();

/**
 * Try to use sessionStorage with fallback to memory
 */
const sessionStorageAvailable = (() => {
  try {
    const testKey = '__lw_session_test__';
    sessionStorage.setItem(testKey, testKey);
    sessionStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
})();

/**
 * Check if cookies are enabled
 */
const cookiesAvailable = (() => {
  try {
    const testKey = '__lw_cookie_test__';
    setCookie(testKey, 'test', 1);
    const result = getCookie(testKey) === 'test';
    deleteCookie(testKey);
    return result;
  } catch (e) {
    return false;
  }
})();

/**
 * In-memory fallback storage (last resort)
 */
let memoryStorage = {};

/**
 * Information about which storage mechanisms are available
 */
export const storageCapabilities = {
  localStorage: localStorageAvailable,
  sessionStorage: sessionStorageAvailable,
  cookies: cookiesAvailable,
  usingFallback: false
};

/**
 * Store UTM data using all available storage methods
 */
export const storeUtmParams = (utmData) => {
  if (!utmData) return false;

  try {
    const utmDataStr = JSON.stringify(utmData);
    let storedSuccessfully = false;
    
    // Try localStorage
    if (localStorageAvailable) {
      try {
        localStorage.setItem(STORAGE_KEYS.LOCAL, utmDataStr);
        storedSuccessfully = true;
      } catch (e) {
        console.warn('Error using localStorage, falling back to other methods:', e);
      }
    }
    
    // Try cookies (always try as additional backup)
    if (cookiesAvailable) {
      try {
        // Cookies have size limitations, so we might need to reduce the data
        // Remove less important fields if the data is too large
        const cookieData = {...utmData};
        delete cookieData.referrer; // Remove if too large
        
        setCookie(STORAGE_KEYS.COOKIE, JSON.stringify(cookieData), COOKIE_EXPIRATION_DAYS);
        storedSuccessfully = true;
      } catch (e) {
        console.warn('Error using cookies:', e);
      }
    }
    
    // Try sessionStorage
    if (sessionStorageAvailable) {
      try {
        sessionStorage.setItem(STORAGE_KEYS.SESSION, utmDataStr);
        storedSuccessfully = true;
      } catch (e) {
        console.warn('Error using sessionStorage:', e);
      }
    }
    
    // Last resort: use memory storage
    if (!storedSuccessfully) {
      memoryStorage.utmData = utmData;
      storageCapabilities.usingFallback = true;
      storedSuccessfully = true;
      console.warn('Using in-memory storage (this will not persist across page refreshes)');
    }
    
    if (storedSuccessfully) {
      return true;
    } else {
      console.error('Failed to store UTM parameters in any storage mechanism');
      return false;
    }
    
  } catch (error) {
    console.error('Error storing UTM parameters:', error);
    return false;
  }
};

/**
 * Get stored UTM data by checking all storage methods
 * Returns null if data is expired or not found
 */
export const getStoredUtmParams = () => {
  try {
    let utmData = null;
    const now = Date.now();
    
    // Try localStorage first
    if (localStorageAvailable) {
      const localData = localStorage.getItem(STORAGE_KEYS.LOCAL);
      if (localData) {
        try {
          const parsedData = JSON.parse(localData);
          // Check if data is expired
          if (parsedData.timestamp && (now - parsedData.timestamp) <= DEFAULT_EXPIRATION_TIME) {
            utmData = parsedData;
          }
        } catch (e) {
          console.warn('Error parsing localStorage UTM data:', e);
        }
      }
    }
    
    // If not found in localStorage, try cookies
    if (!utmData && cookiesAvailable) {
      const cookieData = getCookie(STORAGE_KEYS.COOKIE);
      if (cookieData) {
        try {
          const parsedData = JSON.parse(cookieData);
          // Check if data is expired
          if (parsedData.timestamp && (now - parsedData.timestamp) <= DEFAULT_EXPIRATION_TIME) {
            utmData = parsedData;
          }
        } catch (e) {
          console.warn('Error parsing cookie UTM data:', e);
        }
      }
    }
    
    // If not found in cookies, try sessionStorage
    if (!utmData && sessionStorageAvailable) {
      const sessionData = sessionStorage.getItem(STORAGE_KEYS.SESSION);
      if (sessionData) {
        try {
          utmData = JSON.parse(sessionData);
          // Session storage data doesn't expire within the session
        } catch (e) {
          console.warn('Error parsing sessionStorage UTM data:', e);
        }
      }
    }
    
    // Last resort: check memory storage
    if (!utmData && memoryStorage.utmData) {
      // Check if in-memory data is expired
      if (memoryStorage.utmData.timestamp && 
          (now - memoryStorage.utmData.timestamp) <= DEFAULT_EXPIRATION_TIME) {
        utmData = memoryStorage.utmData;
      }
    }
    
    // If we found data, re-store it to ensure consistency across mechanisms
    if (utmData) {
      // Only re-store if it wasn't from memory (to avoid redundancy)
      if (utmData !== memoryStorage.utmData) {
        storeUtmParams(utmData);
      }
      return utmData;
    }
    
    return null;
  } catch (error) {
    console.error('Error retrieving UTM parameters:', error);
    return null;
  }
};

/**
 * Clear all UTM data from all storage mechanisms
 */
export const clearUtmParams = () => {
  try {
    let cleared = false;
    
    // Clear localStorage
    if (localStorageAvailable) {
      try {
        localStorage.removeItem(STORAGE_KEYS.LOCAL);
        cleared = true;
      } catch (e) {
        console.warn('Error clearing localStorage:', e);
      }
    }
    
    // Clear cookies
    if (cookiesAvailable) {
      try {
        deleteCookie(STORAGE_KEYS.COOKIE);
        cleared = true;
      } catch (e) {
        console.warn('Error clearing cookies:', e);
      }
    }
    
    // Clear sessionStorage
    if (sessionStorageAvailable) {
      try {
        sessionStorage.removeItem(STORAGE_KEYS.SESSION);
        cleared = true;
      } catch (e) {
        console.warn('Error clearing sessionStorage:', e);
      }
    }
    
    // Clear memory storage
    memoryStorage = {};
    
    return cleared;
  } catch (error) {
    console.error('Error clearing UTM parameters:', error);
    return false;
  }
};

/**
 * Main function to handle UTM parameters tracking
 * - Extracts UTM parameters from URL
 * - If present, stores them in all available storage mechanisms
 * - Returns the current UTM data (either from URL or previously stored)
 */
export const trackUtmParameters = () => {
  // First check if the current URL has UTM parameters
  const currentUtmData = extractUtmParams();

  if (currentUtmData) {
    const previous = getStoredUtmParams() || {};
    const firstTouch = Object.fromEntries(
      UTM_PARAMS
        .filter(param => previous[`first_touch_${param}`] || previous[param] || currentUtmData[param])
        .map(param => [
          `first_touch_${param}`,
          previous[`first_touch_${param}`] || previous[param] || currentUtmData[param],
        ]),
    );
    Object.assign(currentUtmData, firstTouch);
    // New UTM parameters found, store them
    storeUtmParams(currentUtmData);
    return currentUtmData;
  }

  // No new UTM parameters, return any previously stored ones
  return getStoredUtmParams();
};

/**
 * Get all UTM parameters for API submission
 * Returns null if no UTM data is available
 */
const mergeUrlParamsIntoData = (baseData = {}) => {
  if (typeof window === 'undefined' || !window.location) {
    return baseData;
  }

  try {
    const urlParams = new URLSearchParams(window.location.search || '');
    if (urlParams.toString()) {
      UTM_PARAMS.forEach(param => {
        if (urlParams.has(param)) {
          baseData[param] = urlParams.get(param);
        }
      });

      if (!baseData.landing_page) {
        baseData.landing_page = window.location.href.split('?')[0];
      }
      if (!baseData.referrer && typeof document !== 'undefined') {
        baseData.referrer = document.referrer || 'direct';
      }
    }
  } catch (error) {
    console.error('Error merging URL params into UTM data:', error);
  }

  return baseData;
};

export const getUtmParamsForApi = () => {
  const storedUtmData = getStoredUtmParams();
  const sanitized = storedUtmData
    ? Object.keys(storedUtmData).reduce((acc, key) => {
        if (key !== 'timestamp') {
          acc[key] = storedUtmData[key];
        }
        return acc;
      }, {})
    : {};

  const merged = mergeUrlParamsIntoData({ ...sanitized });
  const affiliateCode = getAffiliateCodeForApi();
  if (affiliateCode) {
    merged.affiliateCode = affiliateCode;
    merged.affiliate_code = affiliateCode;
    merged.aff = affiliateCode;
  }
  const fbp = getCookie('_fbp');
  const fbc = getCookie('_fbc');
  if (fbp) merged.fbp = fbp;
  if (fbc) merged.fbc = fbc;
  return Object.keys(merged).length ? merged : null;
};

export const getAttributionParams = (utmDataOverride) => {
  const data = utmDataOverride || getUtmParamsForApi();
  if (!data) return {};

  return ATTRIBUTION_PARAMS.reduce((acc, key) => {
    if (data[key]) {
      acc[key] = data[key];
    }
    return acc;
  }, {});
};

/**
 * Get information about the storage method being used
 */
export const getStorageInfo = () => {
  return {
    ...storageCapabilities,
    primaryMethod: localStorageAvailable ? 'localStorage' : 
                  cookiesAvailable ? 'cookies' :
                  sessionStorageAvailable ? 'sessionStorage' : 
                  'memory'
  };
};

/**
 * Detect private browsing mode (not 100% reliable but helps with diagnostics)
 */
export const isPrivateBrowsing = () => {
  return new Promise(resolve => {
    try {
      const yes = () => resolve(true);
      const no = () => resolve(false);
      
      // Safari
      if ('webkitRequestFileSystem' in window) {
        window.webkitRequestFileSystem(
          window.TEMPORARY, 1,
          no,
          yes
        );
        return;
      }
      
      // Firefox
      if ('MozAppearance' in document.documentElement.style) {
        const db = indexedDB.open('test');
        db.onerror = yes;
        db.onsuccess = no;
        return;
      }
      
      // Chrome
      if ('chrome' in window) {
        const fs = window.requestFileSystem || window.webkitRequestFileSystem;
        if (fs) {
          fs(window.TEMPORARY, 100, no, yes);
          return;
        }
      }
      
      // Edge/IE
      if (window.indexedDB && /Edge/.test(navigator.userAgent)) {
        try {
          window.indexedDB.open('test');
          no();
        } catch (e) {
          yes();
        }
        return;
      }
      
      // Other browsers
      no();
    } catch (e) {
      // Default to false if detection fails
      resolve(false);
    }
  });
};
